import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from '../axiosConfig';
import { AxiosError } from 'axios';

export interface Folder {
  id: number;
  name: string;
  organization_id: number;
  slug: string;
}

interface FolderState {
  folders: Folder[];
  currentFolder: Folder | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: FolderState = {
  folders: [],
  currentFolder: null,
  isLoading: false,
  error: null,
};

export const fetchFolders = createAsyncThunk(
  'folders/fetchFolders',
  async (organizationId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/organizations/${organizationId}/folders`
      );
      return response.data.folders;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const createFolder = createAsyncThunk(
  'folders/createFolder',
  async (
    { name, organizationId }: { name: string; organizationId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post('/folders', {
        name,
        organization_id: organizationId,
      });
      return response.data.folder;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const updateFolder = createAsyncThunk(
  'folders/updateFolder',
  async (
    {
      id,
      name,
      organizationId,
    }: { id: number; name: string; organizationId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.put(`/folders/${id}`, {
        name,
        organization_id: organizationId,
      });
      return response.data.folder;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const deleteFolder = createAsyncThunk(
  'folders/deleteFolder',
  async (folderId: number, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/folders/${folderId}`);
      return folderId;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const fetchFolderDetails = createAsyncThunk(
  'folders/fetchFolderDetails',
  async (folderId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/folders/${folderId}`);
      return response.data.folder;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

const folderSlice = createSlice({
  name: 'folders',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFolders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchFolders.fulfilled,
        (state, action: PayloadAction<Folder[]>) => {
          state.isLoading = false;
          state.folders = action.payload;
        }
      )
      .addCase(fetchFolders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createFolder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        createFolder.fulfilled,
        (state, action: PayloadAction<Folder>) => {
          state.isLoading = false;
          state.folders.push(action.payload);
        }
      )
      .addCase(createFolder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateFolder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        updateFolder.fulfilled,
        (state, action: PayloadAction<Folder>) => {
          state.isLoading = false;
          const index = state.folders.findIndex(
            (folder) => folder.id === action.payload.id
          );
          if (index !== -1) {
            state.folders[index] = action.payload;
          }
        }
      )
      .addCase(updateFolder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteFolder.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
       
      })
      .addCase(
        deleteFolder.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.isLoading = false;
          state.folders = state.folders.filter(
            (folder) => folder.id !== action.payload
          );
        }
      )
      .addCase(deleteFolder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchFolderDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchFolderDetails.fulfilled,
        (state, action: PayloadAction<Folder>) => {
          state.isLoading = false;
          state.currentFolder = action.payload;
        }
      )
      .addCase(fetchFolderDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export default folderSlice.reducer;
